import { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Button, Modal, Table, Badge, Form, InputGroup, Row, Col, Spinner, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CaseFile } from '../../types';
import { fetchCaseFiles, deleteCaseFile, bulkDeleteCaseFiles, manuallyMatchFile, updateFileMetadata, fetchFileContent } from '../../api/cases';
import { downloadTransactionsCSV } from '../../utils/exportTransactions';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateFormatters';
import ManualFileMatchModal from './ManualFileMatchModal';
import { denormalizeFileType, normalizeFileType } from '../../utils/fileTypeUtils';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  ColumnFiltersState,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';

interface CaseFilesProps {
  case_id: string;
  onFileDeleted?: () => void;
  requirements?: any[]; // RequiredFile[] but avoiding circular import
  onFileMatched?: () => void;
  canDeleteFile?: (file: CaseFile) => boolean; // Optional permission check for deletion
  canBulkDelete?: boolean; // Optional flag to show/hide bulk delete
  hideManualMatch?: boolean; // Optional flag to hide manual match button
}

// Extend ColumnMeta
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends unknown, TValue> {
    filterComponent?: (column: any) => React.ReactNode;
  }
}

// Create a column helper
const columnHelper = createColumnHelper<CaseFile>();

const isCourtSyncFile = (file: CaseFile): boolean =>
  !!file.court_document_id || file.classification_method === 'court_sync';

// File type options for the dropdown (UI display names)
const FILE_TYPE_OPTIONS = [
  'Bank Statements',
  'Bank credit (deposit)',
  'Bank debit (withdrawal)',
  'Bank signature card',
  'Check Images',
  'Check register',
  'Deposit image',
  'Financial statement - Balance sheet',
  'Financial statement - Income statement',
  'Financial statement - Statement of cash flows',
  'Financial statement - Other',
  'IRS Tax form 1040',
  'IRS Tax form 1065',
  'IRS Tax form 1099',
  'IRS Tax form 1120',
  'IRS Tax form 1120S',
  'IRS Tax form 941',
  'IRS Tax form K-1',
  'IRS Tax form Other',
  'IRS Tax form W2',
  'IRS Tax form W4',
  'Appraisal',
  'Correspondence',
  'Credit card statement',
  'Deeds and titles',
  'Depreciation schedule',
  'Email',
  'Expense report',
  'General ledger',
  'Investment account statement',
  'Invoice',
  'Lease agreement',
  'Legal document',
  'Loan application',
  'Loan statement',
  'Note payable',
  'Trial balance',
  'Wire transfer',
  'Other',
].sort();

// Custom filter component for select dropdowns
const SelectFilter = ({ column, options }: { column: any; options: string[] }) => {
  return (
    <Form.Select
      size="sm"
      value={column.getFilterValue() || ''}
      onChange={(e) => column.setFilterValue(e.target.value || undefined)}
    >
      <option value="">All</option>
      {options.map(option => (
        <option key={option} value={option}>{option}</option>
      ))}
    </Form.Select>
  );
};

const CaseFiles = forwardRef<{ loadFiles: () => void }, CaseFilesProps>(({ case_id, onFileDeleted, requirements = [], onFileMatched, canDeleteFile, canBulkDelete = true, hideManualMatch = false }, ref) => {
  const [files, setFiles] = useState<CaseFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<CaseFile | null>(null);
  const [deleteStatements, setDeleteStatements] = useState(false);
  const [showManualMatch, setShowManualMatch] = useState(false);
  const [fileToMatch, setFileToMatch] = useState<CaseFile | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showConfirmBulkDelete, setShowConfirmBulkDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'upload_date', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [editingFileType, setEditingFileType] = useState<string | null>(null); // file_id being edited
  const [updatingFileType, setUpdatingFileType] = useState(false);
  const [showCourtFiles, setShowCourtFiles] = useState(true);
  const auth = useAuth();

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await fetchCaseFiles(case_id, auth.user?.id_token);
      // Filter out archived files
      const activeFiles = response.data.files
        .filter((file: CaseFile) => !file.archived);
      setFiles(activeFiles);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (file_id: string) => {
    try {
      await deleteCaseFile(case_id, file_id, auth.user?.id_token, deleteStatements);
      loadFiles();
      setShowConfirmDelete(false);
      setFileToDelete(null);
      setDeleteStatements(false);
      if (onFileDeleted) {
        onFileDeleted();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const initiateDelete = (file: CaseFile) => {
    setFileToDelete(file);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    
    await handleDelete(fileToDelete.file_id);
    setShowConfirmDelete(false);
    setFileToDelete(null);
  };

  const handleManualMatch = async (file_id: string, requirement_id: string, month?: string) => {
    try {
      await manuallyMatchFile(case_id, file_id, requirement_id, month, auth.user?.id_token);
      await loadFiles(); // Refresh files list
      if (onFileMatched) onFileMatched();
    } catch (error) {
      console.error('Error manually matching file:', error);
      throw error;
    }
  };

  const initiateManualMatch = (file: CaseFile) => {
    setFileToMatch(file);
    setShowManualMatch(true);
  };

  const handleFileTypeUpdate = async (file_id: string, newFileType: string) => {
    try {
      setUpdatingFileType(true);
      const normalizedType = normalizeFileType(newFileType);
      await updateFileMetadata(case_id, file_id, { file_type: normalizedType }, auth.user?.id_token);
      await loadFiles(); // Refresh files list
      setEditingFileType(null);
    } catch (error) {
      console.error('Error updating file type:', error);
      alert('Failed to update file type. Please try again.');
    } finally {
      setUpdatingFileType(false);
    }
  };

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && editingFileType) {
        setEditingFileType(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editingFileType]);

  // Multi-select handlers
  const toggleFileSelection = (file_id: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(file_id)) {
        newSet.delete(file_id);
      } else {
        newSet.add(file_id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const currentPageFileIds = table.getRowModel().rows.map(row => row.original.file_id);
    const allCurrentSelected = currentPageFileIds.every(id => selectedFiles.has(id));
    
    if (allCurrentSelected) {
      // Deselect all on current page
      setSelectedFiles(prev => {
        const newSet = new Set(prev);
        currentPageFileIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all on current page
      setSelectedFiles(prev => {
        const newSet = new Set(prev);
        currentPageFileIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  const initiateBulkDelete = () => {
    if (selectedFiles.size === 0) return;
    setShowConfirmBulkDelete(true);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const token = auth.user?.id_token;
      await downloadTransactionsCSV(
        case_id,
        token ?? '',
        files,
        selectedFiles.size > 0 ? selectedFiles : undefined,
      );
    } catch (err: any) {
      alert(err?.message ?? 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const fetchDownloadBlob = async (file: CaseFile): Promise<Blob> => {
    const response = await fetchFileContent(case_id, file.file_id, auth.user?.id_token);
    const viewUrl = response.data?.view_url;
    if (!viewUrl) {
      throw new Error(`No download URL returned for ${file.filename}`);
    }

    const blobResp = await fetch(viewUrl);
    if (!blobResp.ok) {
      throw new Error(`Failed to download ${file.filename}`);
    }

    return await blobResp.blob();
  };

  const handleDownloadSingle = async (file: CaseFile) => {
    try {
      setDownloadingFileId(file.file_id);
      const blob = await fetchDownloadBlob(file);
      saveAs(blob, file.filename || `file-${file.file_id}`);
    } catch (err: any) {
      console.error('Error downloading file:', err);
      alert(err?.message ?? 'Failed to download file. Please try again.');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDownloadZip = async () => {
    const filesToDownload = selectedFiles.size > 0
      ? files.filter((f) => selectedFiles.has(f.file_id))
      : files;

    if (filesToDownload.length === 0) {
      return;
    }

    try {
      setIsDownloadingZip(true);
      const zip = new JSZip();

      for (const file of filesToDownload) {
        try {
          const blob = await fetchDownloadBlob(file);
          const zipName = file.filename || `file-${file.file_id}`;
          zip.file(zipName, blob);
        } catch (err) {
          console.error(`Skipping file ${file.file_id} during ZIP download`, err);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipSuffix = selectedFiles.size > 0 ? `selected-${selectedFiles.size}` : 'all';
      saveAs(zipBlob, `case-${case_id}-files-${zipSuffix}.zip`);
    } catch (err: any) {
      console.error('Error building ZIP:', err);
      alert(err?.message ?? 'Failed to build ZIP download. Please try again.');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedFiles.size === 0) return;
    
    setIsDeleting(true);
    try {
      await bulkDeleteCaseFiles(case_id, Array.from(selectedFiles), auth.user?.id_token);
      setSelectedFiles(new Set());
      loadFiles();
      setShowConfirmBulkDelete(false);
      if (onFileDeleted) {
        onFileDeleted();
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('Failed to delete some files. Check console for details.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper function to get status badge (only show ONE badge)
  const getStatusBadge = (file: CaseFile) => {
    // Priority order: Error > Processing > Classifying > Matched > Auto-Classified/Approved > Pending
    
    // 1. Error state (highest priority)
    if (file.status === 'error' || file.processing_status === 'error') {
      const errorMessage = file.classification_error || 'This file could not be processed.';
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{errorMessage}</Tooltip>}>
          <Badge bg="danger" style={{ cursor: 'help' }}>Error</Badge>
        </OverlayTrigger>
      );
    }
    
    // 2. Processing state
    if (file.status === 'processing' || file.processing_status === 'processing') {
      return (
        <Badge bg="info">
          <Spinner animation="border" size="sm" className="me-2" />
          Processing
        </Badge>
      );
    }
    
    // 3. Classifying state
    if (file.classification_status === 'pending') {
      return (
        <Badge bg="secondary">
          <Spinner animation="border" size="sm" className="me-2" />
          Classifying...
        </Badge>
      );
    }
    
    // 4. Matched to requirements (completed state)
    if (file.requirement_match_status === 'matched' && file.matched_requirements && file.matched_requirements.length > 0) {
      return <Badge bg="success">Matched ({file.matched_requirements.length})</Badge>;
    }
    
    // 5. Auto-Classified (completed state)
    if (file.classification_status === 'classified' && file.classification_method === 'auto_detected') {
      return <Badge bg="info">Auto-Classified</Badge>;
    }
    
    // 6. User Approved (completed state)
    if (file.classification_status === 'approved') {
      return <Badge bg="success">Approved</Badge>;
    }
    
    // 7. Default pending state
    return <Badge bg="secondary">Pending</Badge>;
  };

  // Get unique file types for filter
  const uniqueFileTypes = useMemo(() => 
    [...new Set(files.map(f => f.file_type).filter(Boolean))].sort(),
    [files]
  );

  // Get unique display statuses for filter (user-friendly labels)
  const uniqueStatuses = useMemo(() => {
    const statusSet = new Set<string>();
    files.forEach(file => {
      // Match the logic in getStatusBadge to get display labels
      if (file.status === 'error' || file.processing_status === 'error') {
        statusSet.add('Error');
      } else if (file.status === 'processing' || file.processing_status === 'processing') {
        statusSet.add('Processing');
      } else if (file.classification_status === 'pending') {
        statusSet.add('Classifying');
      } else if (file.requirement_match_status === 'matched' && file.matched_requirements && file.matched_requirements.length > 0) {
        statusSet.add('Matched');
      } else if (file.classification_status === 'classified' && file.classification_method === 'auto_detected') {
        statusSet.add('Auto-Classified');
      } else if (file.classification_status === 'approved') {
        statusSet.add('Approved');
      } else {
        statusSet.add('Pending');
      }
    });
    return Array.from(statusSet).sort();
  }, [files]);

  // Define columns with TanStack Table
  const columns = useMemo(() => [
    ...(canBulkDelete ? [columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <Form.Check
          type="checkbox"
          checked={table.getRowModel().rows.length > 0 && table.getRowModel().rows.every(row => selectedFiles.has(row.original.file_id))}
          onChange={toggleSelectAll}
        />
      ),
      cell: ({ row }) => (
        isCourtSyncFile(row.original) ? null : (
          <Form.Check
            type="checkbox"
            checked={selectedFiles.has(row.original.file_id)}
            onChange={() => toggleFileSelection(row.original.file_id)}
          />
        )
      ),
      enableSorting: false,
      enableColumnFilter: false,
    })] : []),
    columnHelper.accessor('filename', {
      header: 'File Name',
      cell: info => (
        <Link to={`/case/${case_id}/file/${info.row.original.file_id}`}>
          {info.getValue()}
        </Link>
      ),
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.accessor('file_type', {
      header: 'File Type',
      cell: info => {
        const fileType = info.getValue();
        const isClassifying = info.row.original.classification_status === 'pending';
        const file_id = info.row.original.file_id;
        const isEditing = editingFileType === file_id;
        const isCourtSync = isCourtSyncFile(info.row.original);

        if (isCourtSync) {
          return (
            <div>
              <div className="d-flex align-items-center gap-2">
                <span>{denormalizeFileType(fileType) || fileType}</span>
                <Badge bg="secondary" title="Downloaded automatically from the Indiana court docket">Court Docket</Badge>
              </div>
              {info.row.original.page_count && info.row.original.page_count > 1 && (
                <div><small className="text-muted">{info.row.original.page_count} pages</small></div>
              )}
            </div>
          );
        }

        if (isEditing) {
          return (
            <div 
              style={{ minWidth: '200px' }}
              onBlur={(e) => {
                // Only close if clicking outside the dropdown
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setTimeout(() => setEditingFileType(null), 100);
                }
              }}
            >
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  size="sm"
                  value={denormalizeFileType(fileType)}
                  onChange={(e) => {
                    handleFileTypeUpdate(file_id, e.target.value);
                  }}
                  disabled={updatingFileType}
                  autoFocus
                  style={{ flex: 1 }}
                >
                  {FILE_TYPE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 text-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFileType(null);
                  }}
                  title="Cancel"
                  disabled={updatingFileType}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </div>
            </div>
          );
        }
        
        return (
          <div>
            {!isClassifying && (
              <div className="d-flex align-items-center gap-2">
                <span>{denormalizeFileType(fileType) || fileType}</span>
                <i 
                  className="bi bi-pencil text-secondary" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingFileType(file_id);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{ 
                    fontSize: '0.9rem', 
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                  title="Edit file type"
                />
              </div>
            )}
            {info.row.original.classification_method === 'auto_detected' && !isClassifying && (
              <Badge bg="info" className="ms-2" title={`Auto-detected by ${info.row.original.classified_by || 'system'}`}>
                AI
              </Badge>
            )}
            {isClassifying && (
              <Badge bg="secondary">Classifying...</Badge>
            )}
            {info.row.original.page_count && info.row.original.page_count > 1 && (
              <div><small className="text-muted">{info.row.original.page_count} pages</small></div>
            )}
          </div>
        );
      },
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
      meta: {
        filterComponent: (column: any) => (
          <SelectFilter 
            column={column} 
            options={uniqueFileTypes} 
          />
        ),
      },
    }),
    columnHelper.accessor(row => {
      // Return the display status label for filtering
      if (row.status === 'error' || row.processing_status === 'error') return 'Error';
      if (row.status === 'processing' || row.processing_status === 'processing') return 'Processing';
      if (row.classification_status === 'pending') return 'Classifying';
      if (row.requirement_match_status === 'matched' && row.matched_requirements && row.matched_requirements.length > 0) return 'Matched';
      if (row.classification_status === 'classified' && row.classification_method === 'auto_detected') return 'Auto-Classified';
      if (row.classification_status === 'approved') return 'Approved';
      return 'Pending';
    }, {
      id: 'status',
      header: 'Status',
      cell: info => getStatusBadge(info.row.original),
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
      meta: {
        filterComponent: (column: any) => (
          <SelectFilter 
            column={column} 
            options={uniqueStatuses} 
          />
        ),
      },
    }),
    columnHelper.accessor(row => formatDateTime(row.upload_date), {
      id: 'upload_date',
      header: 'Upload Date',
      cell: info => info.getValue(),
      sortingFn: (rowA, rowB) => {
        // Sort by raw date value for proper chronological order
        const dateA = new Date(rowA.original.upload_date).getTime();
        const dateB = new Date(rowB.original.upload_date).getTime();
        return dateA - dateB;
      },
    }),
    columnHelper.accessor('uploaded_by', {
      header: 'Uploaded By',
      cell: info => info.getValue(),
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: 'includesString',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const isCourtSync = isCourtSyncFile(row.original);
        const canDelete = !isCourtSync && (canDeleteFile ? canDeleteFile(row.original) : true);
        const showMatch = !isCourtSync && !hideManualMatch && requirements.length > 0;
        
        return (
          <div className="d-flex gap-2">
            {showMatch && (
              <Button 
                variant="outline-primary"
                size="sm"
                onClick={() => initiateManualMatch(row.original)}
              >
                Match
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleDownloadSingle(row.original)}
              disabled={downloadingFileId === row.original.file_id}
            >
              {downloadingFileId === row.original.file_id ? 'Downloading...' : 'Download'}
            </Button>
            {canDelete && (
              <Button 
                variant="danger" 
                size="sm"
                onClick={() => initiateDelete(row.original)}
              >
                Delete
              </Button>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
    }),
  ], [case_id, requirements, selectedFiles, uniqueFileTypes, uniqueStatuses, canDeleteFile, hideManualMatch, editingFileType, updatingFileType]);

  const visibleFiles = useMemo(
    () => showCourtFiles ? files : files.filter(f => !isCourtSyncFile(f)),
    [files, showCourtFiles]
  );

  // Create the table instance
  const table = useReactTable({
    data: visibleFiles,
    columns,
    state: {
      columnFilters,
      globalFilter,
      sorting,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableGlobalFilter: true,
    globalFilterFn: 'includesString',
    autoResetPageIndex: false,
  });

  // Expose loadFiles method via ref
  useImperativeHandle(ref, () => ({
    loadFiles
  }));

  useEffect(() => {
    loadFiles();
  }, [case_id]);

  // Polling: periodically refresh only status/category fields so the table updates live
  const needsPolling = useMemo(() => {
    return files.some(f =>
      (f.classification_status === 'pending') ||
      (f.status === 'processing')
    );
  }, [files]);

  useEffect(() => {
    if (!case_id) return;
    if (!needsPolling) return;

    let intervalId = window.setInterval(async () => {
      try {
        const response = await fetchCaseFiles(case_id, auth.user?.id_token);
        const latestFiles: CaseFile[] = (response.data.files || []).filter((file: CaseFile) => !file.archived);
        const latestMap = new Map(latestFiles.map(f => [f.file_id, f]));

        setFiles(prev =>
          prev.map(f => {
            const updated = latestMap.get(f.file_id);
            if (!updated) return f;
            // Only merge status/category-related fields to avoid re-rendering entire rows unnecessarily
            return {
              ...f,
              status: updated.status,
              classification_status: updated.classification_status,
              file_type: updated.file_type,
              processing_status: updated.processing_status,
              requirement_match_status: updated.requirement_match_status,
              classification_method: updated.classification_method,
              matched_requirements: updated.matched_requirements || f.matched_requirements,
            };
          })
        );
      } catch (err) {
        console.error('Polling error fetching file statuses:', err);
      }
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [case_id, needsPolling, auth.user?.id_token]);

  if (loading) return <div>Loading files...</div>;
  if (error) return <div className="text-danger">Error: {error}</div>;

  return (
    <div>
      <style>{`.court-sync-row > * { background-color: #e9ecef !important; }`}</style>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0 d-inline-block me-3">Case Files</h3>
          <Badge bg="secondary">
            {table.getFilteredRowModel().rows.length} of {files.length} file{files.length !== 1 ? 's' : ''}
          </Badge>
          {selectedFiles.size > 0 && (
            <Badge bg="primary" className="ms-2">{selectedFiles.size} selected</Badge>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-success"
            size="sm"
            onClick={handleExportCSV}
            disabled={isExporting || files.length === 0}
            title={selectedFiles.size > 0 ? `Export transactions from ${selectedFiles.size} selected file(s)` : 'Export all extracted transactions as CSV'}
          >
            {isExporting ? (
              <><Spinner animation="border" size="sm" className="me-1" />Exporting…</>
            ) : selectedFiles.size > 0 ? (
              `Export CSV (${selectedFiles.size} selected)`
            ) : (
              'Export Transactions CSV'
            )}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownloadZip}
            disabled={isDownloadingZip || files.length === 0}
            title={selectedFiles.size > 0 ? `Download ${selectedFiles.size} selected file(s) as ZIP` : 'Download all files as ZIP'}
          >
            {isDownloadingZip ? (
              <><Spinner animation="border" size="sm" className="me-1" />Preparing ZIP...</>
            ) : selectedFiles.size > 0 ? (
              `Download ZIP (${selectedFiles.size} selected)`
            ) : (
              'Download All ZIP'
            )}
          </Button>
          {canBulkDelete && selectedFiles.size > 0 && (
            <Button
              variant="danger"
              size="sm"
              onClick={initiateBulkDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : `Delete Selected (${selectedFiles.size})`}
            </Button>
          )}
        </div>
      </div>

      {/* Global search and clear filters */}
      <Row className="mb-3 align-items-center">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>🔍</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search all columns..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            {globalFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => setGlobalFilter('')}
              >
                ✕
              </Button>
            )}
          </InputGroup>
          {files.some(isCourtSyncFile) && (
            <Form.Check
              type="switch"
              id="show-court-files-switch"
              label="Show court docket files"
              checked={showCourtFiles}
              onChange={(e) => setShowCourtFiles(e.target.checked)}
              className="small mt-2"
            />
          )}
        </Col>
        <Col md={6} className="text-end">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={loadFiles}
            disabled={loading}
            className="me-2"
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setColumnFilters([]);
              setGlobalFilter('');
              setSorting([{ id: 'upload_date', desc: true }]);
            }}
          >
            Clear All Filters
          </Button>
        </Col>
      </Row>

      {/* Pagination controls - Top */}
      <Row className="mb-3">
        <Col md={6} className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            ««
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            «
          </Button>
          <span className="small">
            Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
            <strong>{table.getPageCount()}</strong>
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            »
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            »»
          </Button>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end gap-2">
          <span className="small">Show:</span>
          <Form.Select
            size="sm"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            style={{ width: 'auto' }}
          >
            {[10, 25, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize} rows
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped hover size="sm">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {/* Column header with sorting */}
                    <div 
                      style={{ 
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.getCanSort() && (
                        <span style={{ fontSize: '0.8em', opacity: 0.6 }}>
                          {{
                            asc: '↑',
                            desc: '↓',
                          }[header.column.getIsSorted() as string] ?? '↕'}
                        </span>
                      )}
                    </div>
                    
                    {/* Column filter */}
                    {header.column.getCanFilter() && (
                      <div className="mt-1">
                        {header.column.columnDef.meta?.filterComponent ? (
                          header.column.columnDef.meta.filterComponent(header.column)
                        ) : (
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder={`Filter...`}
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value)}
                          />
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} className={isCourtSyncFile(row.original) ? 'court-sync-row' : undefined}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* No results message */}
      {table.getFilteredRowModel().rows.length === 0 && files.length > 0 && (
        <div className="alert alert-warning mt-3">
          No files match your current filters.
        </div>
      )}

      {files.length === 0 && (
        <div className="alert alert-info mt-3">
          No files uploaded yet.
        </div>
      )}

      {/* Pagination controls - Bottom */}
      <Row className="mt-3">
        <Col md={6} className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            ««
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            «
          </Button>
          <span className="small">
            Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
            <strong>{table.getPageCount()}</strong>
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            »
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            »»
          </Button>
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end gap-2">
          <span className="small">Show:</span>
          <Form.Select
            size="sm"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            style={{ width: 'auto' }}
          >
            {[10, 25, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                {pageSize} rows
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Modals - unchanged */}
      <Modal show={showConfirmDelete} onHide={() => setShowConfirmDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete "{fileToDelete?.filename}"?</p>
          {fileToDelete?.file_type === 'bank-statements' && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="deleteStatements"
                checked={deleteStatements}
                onChange={(e) => setDeleteStatements(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="deleteStatements">
                Also delete all bank statements extracted from this file
              </label>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <ManualFileMatchModal
        show={showManualMatch}
        onHide={() => {
          setShowManualMatch(false);
          setFileToMatch(null);
        }}
        onSubmit={handleManualMatch}
        file={fileToMatch}
        requirements={requirements}
      />

      <Modal show={showConfirmBulkDelete} onHide={() => setShowConfirmBulkDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{selectedFiles.size}</strong> selected file(s)?</p>
          <p className="text-muted small">
            This will also delete any associated bank statements and check images from these files.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmBulkDelete(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmBulkDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : `Delete ${selectedFiles.size} File(s)`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default CaseFiles;
